import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useImporterExcel } from '../hooks/useEmployes';
import { employesApi } from '../api/employes.api';

export function EmployeImportExcelPage() {
  const navigate = useNavigate();
  const importer = useImporterExcel();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fichierSelectionne, setFichierSelectionne] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [erreurFichier, setErreurFichier] = useState<string | null>(null);
  const [isTelechargement, setIsTelechargement] = useState(false);
  const [erreurTelechargement, setErreurTelechargement] = useState<string | null>(null);

  const handleFichierSelect = (f: File | null) => {
    if (!f) return;
    if (!f.name.endsWith('.xlsx')) {
      setErreurFichier('Seuls les fichiers .xlsx sont acceptés');
      setFichierSelectionne(null);
      return;
    }
    setErreurFichier(null);
    setFichierSelectionne(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFichierSelect(e.dataTransfer.files[0] ?? null);
  };

  const handleImport = async () => {
    if (!fichierSelectionne) return;
    await importer.mutateAsync(fichierSelectionne);
  };

  const handleTelechargerTemplate = async () => {
    setIsTelechargement(true);
    setErreurTelechargement(null);
    try {
      await employesApi.telechargerTemplateExcel();
    } catch (e: unknown) {
      setErreurTelechargement(e instanceof Error ? e.message : 'Erreur lors du téléchargement du modèle');
    } finally {
      setIsTelechargement(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-content mb-2">Importer des employés depuis Excel</h1>
      <p className="text-content-muted mb-6">
        Importez une liste d'employés à partir d'un fichier Excel respectant le format requis.
      </p>

      {/* Description du format */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h2 className="text-sm font-semibold text-blue-900 mb-2">Format attendu du fichier Excel</h2>
        <table className="text-xs text-blue-800 w-full border-collapse">
          <tbody>
            <tr>
              <td className="font-medium pr-3 py-0.5">Cellule A1</td>
              <td><code>"Projet:"</code> | B1 : Nom du projet (ex : PNASI)</td>
            </tr>
            <tr>
              <td className="font-medium pr-3 py-0.5">Ligne 2</td>
              <td>Lieu d'implémentation | Date Début | Date Fin du projet</td>
            </tr>
            <tr>
              <td className="font-medium pr-3 py-0.5">Ligne 3</td>
              <td>En-têtes : NOM ET PRENOMS | SALAIRE_BASE | DATE_EMBAUCHE | FONCTION | IND_TRANSPORT | IND_COMMUNICATION | EMAIL | CONTRIBUTION_SALAIRE</td>
            </tr>
            <tr>
              <td className="font-medium pr-3 py-0.5">Ligne 4+</td>
              <td>Données employés</td>
            </tr>
          </tbody>
        </table>
        <p className="text-xs text-blue-700 mt-2">
          NOM ET PRENOMS et SALAIRE_BASE sont obligatoires. Le projet est créé automatiquement s'il n'existe pas.
          Les doublons sont ignorés. <strong>CONTRIBUTION_SALAIRE</strong> : indiquer <code>OUI</code> si ce montant est une participation du projet au salaire (non traité en paie).
        </p>
      </div>

      <div className="bg-surface rounded-lg border border-border p-6 space-y-5">
        {/* Bouton télécharger le modèle */}
        <button
          onClick={handleTelechargerTemplate}
          disabled={isTelechargement}
          className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-700 rounded font-medium hover:bg-green-50 disabled:opacity-50 transition-colors"
        >
          {isTelechargement ? 'Téléchargement...' : '⬇ Télécharger le modèle Excel'}
        </button>

        {erreurTelechargement && (
          <p className="text-danger text-sm bg-danger-light p-3 rounded">{erreurTelechargement}</p>
        )}

        {/* Zone drag & drop */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-border hover:border-border-strong'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={e => handleFichierSelect(e.target.files?.[0] ?? null)}
          />
          <p className="text-content-muted text-sm">
            {fichierSelectionne ? (
              <span className="text-accent font-medium">📊 {fichierSelectionne.name}</span>
            ) : (
              <>Glissez un fichier <strong>.xlsx</strong> ici ou <span className="text-accent">parcourir</span></>
            )}
          </p>
        </div>

        {erreurFichier && (
          <p className="text-danger text-sm bg-danger-light p-3 rounded">{erreurFichier}</p>
        )}

        {/* Résultat */}
        {importer.data && (
          <div className="p-4 bg-success-light rounded border border-success space-y-2">
            <p className="font-semibold text-success">Import terminé</p>
            <p className="text-sm text-success">Projet : <strong>{importer.data.nomProjet}</strong></p>
            <p className="text-sm text-success">Employés créés : <strong>{importer.data.crees}</strong></p>
            {importer.data.ignores.length > 0 && (
              <div>
                <p className="text-sm font-medium text-amber-700">Ignorés (doublons) : {importer.data.ignores.length}</p>
                <ul className="mt-1 text-xs text-amber-600 space-y-0.5 list-disc list-inside">
                  {importer.data.ignores.map((nom, i) => (
                    <li key={i}>{nom}</li>
                  ))}
                </ul>
              </div>
            )}
            {importer.data.erreurs.length > 0 && (
              <div>
                <p className="text-sm font-medium text-danger">Erreurs : {importer.data.erreurs.length}</p>
                <ul className="mt-1 text-xs text-danger space-y-0.5 list-disc list-inside">
                  {importer.data.erreurs.map((e, i) => (
                    <li key={i}>Ligne {e.ligne} : {e.message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {importer.isError && (
          <p className="text-danger text-sm bg-danger-light p-3 rounded">
            {(importer.error as any)?.response?.data?.message ?? (importer.error as any)?.message ?? 'Erreur lors de l\'import'}
          </p>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleImport}
            disabled={!fichierSelectionne || importer.isPending}
            className="flex-1 bg-blue-600 text-white py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {importer.isPending ? 'Import en cours...' : 'Importer'}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-border rounded text-sm hover:bg-surface-raised"
          >
            Retour
          </button>
        </div>
      </div>
    </div>
  );
}
